import { Suspense, lazy, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import { BookingProvider } from "@/components/site/BookingContext";
import Home from "@/pages/Home";

// Route-level code splitting: everything but the landing page (the entry
// point almost everyone hits first) loads in its own chunk on demand,
// instead of one large bundle shipped up front for every visitor.
const CaseStudiesPage = lazy(() => import("@/pages/CaseStudiesPage"));
const CustomAIBuildsPage = lazy(() => import("@/pages/CustomAIBuildsPage"));
const InsightsPage = lazy(() => import("@/pages/InsightsPage"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const LiveDemoPage = lazy(() => import("@/pages/LiveDemoPage"));
const ServicePage = lazy(() => import("@/pages/ServicePage"));

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      // Give the (possibly lazy-loaded) page a moment to mount before we
      // look for the target element. Sections on the home page behind
      // LazyMount don't exist in the DOM at all until they force-mount off
      // this same hash (see LazyMount's anchorId prop) — that force-mount
      // still has to wait on the lazy chunk's import(), so the window here
      // is longer than a plain "already in the DOM" retry would need.
      const id = hash.slice(1);
      let attempts = 0;
      let cancelled = false;
      const tryScroll = () => {
        const el = document.getElementById(id);
        if (el) {
          settleScroll(el);
        } else if (attempts < 60) {
          attempts += 1;
          setTimeout(tryScroll, 50);
        }
      };
      // The target existing isn't enough on its own: neighboring LazyMount
      // sections can still be resolving from placeholder to real height
      // around the same time (their own force-mount or IntersectionObserver
      // firing off the same load/scroll), which keeps moving the target's
      // position for a bit — reproduced with footer links and deep
      // direct-hash loads, where a single scrollIntoView landed on wherever
      // the target happened to be at that instant, not where it settled.
      // Instant ("auto") scrolling throughout avoids the ambiguity a smooth
      // scroll's own travel time would add when checking whether a follow-up
      // move is a real layout shift or just the previous scroll still
      // animating; re-checking position a few times over ~1.5s catches
      // sections that resolve a beat after the target does.
      //
      // That correction has to stop the instant it's the user moving the
      // page, not a layout shift — otherwise scrolling away within that
      // ~1.5s window (completely normal right after tapping a nav link)
      // reads as "target drifted" and gets yanked back, fighting a
      // deliberate scroll. Shipped without this check once already: a real
      // wheel/touch/key scroll during that window is what has to cancel the
      // loop, not just checking whether the target's position looks stable.
      const settleScroll = (el) => {
        let corrections = 0;
        let stableStreak = 0;
        const stop = () => {
          cancelled = true;
          userInputEvents.forEach((type) => window.removeEventListener(type, stop));
        };
        const userInputEvents = ["wheel", "touchstart", "keydown", "pointerdown"];
        userInputEvents.forEach((type) => window.addEventListener(type, stop, { passive: true, once: true }));
        const check = () => {
          if (cancelled) return;
          const top = el.getBoundingClientRect().top;
          if (Math.abs(top) > 2) {
            el.scrollIntoView({ behavior: "auto", block: "start" });
            corrections += 1;
            stableStreak = 0;
          } else {
            stableStreak += 1;
          }
          if (stableStreak < 4 && corrections < 20) {
            setTimeout(check, 100);
          }
        };
        check();
      };
      tryScroll();
      return () => {
        cancelled = true;
      };
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

function App() {
  return (
    <HelmetProvider>
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <BookingProvider>
          <Suspense fallback={null}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/case-studies" element={<CaseStudiesPage />} />
              <Route path="/custom-ai-builds" element={<CustomAIBuildsPage />} />
              <Route path="/insights" element={<InsightsPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/live-demo" element={<LiveDemoPage />} />
              <Route path="*" element={<ServicePage />} />
            </Routes>
          </Suspense>
        </BookingProvider>
      </BrowserRouter>
      <Toaster position="top-center" theme="light" richColors />
    </div>
    </HelmetProvider>
  );
}

export default App;
